import { useEffect, useLayoutEffect, useState } from "react";

/*
 * A list that scrolls inside its card rather than taking the page with it.
 *
 * Written for Configurations, where the rail of masters beside the rows is how
 * you move between lists and a rail that scrolls away with a long list is a
 * rail you have to scroll back up to use. Every pharmacy list wants the same
 * thing for the same reason: the screen's own title, its filters and the pager
 * stay where they are, and only the rows move.
 *
 * Hand it a ref on the element wrapping the table, and the things that change
 * what is around the rows - the page, the row count, whether the spinner is
 * still up. It returns the height to give antd's `scroll.y`.
 */

/* Breathing room between the card and the bottom of the window. */
const PAGE_GAP = 24;
const MIN_ROWS_HEIGHT = 220;

/*
 * How tall the scrolling rows are allowed to be, so the whole card sits on
 * screen whatever the screen is and only the rows inside it scroll.
 *
 * Nothing about the chrome is assumed - not the app header, not the card's own
 * title and search strip, not the pager or the add bar under the rows. Two
 * measurements settle it, and neither depends on the answer:
 *
 *   where the rows start   the scrolling box's own top edge, fixed by whatever
 *                          is above it
 *   what is below them     the card's bottom less the table's, which is the
 *                          pager and the add bar whatever they happen to be
 *
 * The room left between them is the height. This used to be solved by trial -
 * measure the card, take back the overshoot, measure again - which is a state
 * feeding the measurement that sets it: on any pass where the card did not
 * move, it never settled and React stopped it as an infinite update.
 */
export const useFittedRowsHeight = (tableRef, deps) => {
  const [height, setHeight] = useState(MIN_ROWS_HEIGHT);
  /* Bumped on resize so the measurement runs again. */
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onResize = () => setTick((n) => n + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useLayoutEffect(() => {
    const root = tableRef.current;
    /*
     * The card the rows sit in, where there is one - its bottom edge is what
     * tells us about anything under the table that still has to fit, like
     * Configurations' add bar. A table standing on its own measures against
     * its own wrapper, which carries the pager and nothing else.
     */
    const card = root?.closest("section") || root;
    const table = root?.querySelector(".ant-table");
    const body = root?.querySelector(".ant-table-body");
    if (!card || !table || !body) return;

    const rowsTop = body.getBoundingClientRect().top;
    const belowRows =
      card.getBoundingClientRect().bottom - table.getBoundingClientRect().bottom;

    const next = Math.max(
      MIN_ROWS_HEIGHT,
      Math.round(window.innerHeight - PAGE_GAP - rowsTop - belowRows)
    );

    /* Within a pixel of where it already is, leave it alone - an identical
       value is what keeps this from scheduling another render. */
    setHeight((prev) => (Math.abs(prev - next) <= 1 ? prev : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return height;
};

export default useFittedRowsHeight;
